/**
 * What a drop of one or more files means for the image-subtitle path, decided from names and the
 * first bytes of each file.
 *
 *   `.sup` (PGS)                       → recognise it
 *   `.idx` + VobSub `.sub`, together   → recognise them, paired by base name
 *   a VobSub `.sub` alone              → ask for its `.idx`, which holds the timings
 *   an `.idx` alone                    → ask for its `.sub`, which holds the pictures
 *   a MicroDVD `.sub` (text)           → not ours: it goes to the MicroDVD reader as before
 *
 * The `.sub` is the ambiguous one, and `sniffBitmapFile` in `src/formats/classify.ts` is the one
 * place that tells a VobSub program stream (`00 00 01 BA`) from MicroDVD text. This module asks it
 * rather than keeping a second rule.
 *
 * Pure: the page reads each file's head and calls `imageDrop`.
 */

import { extensionOf, sniffBitmapFile } from '../formats/classify.ts';

/** One dropped file as this module sees it: its name and its first bytes (16 are plenty). */
export interface DroppedHead<F> {
  readonly file: F;
  readonly name: string;
  readonly head: Uint8Array;
}

export type ImageDrop<F> =
  /** Nothing here is an image subtitle; the drop goes where it went before. */
  | { readonly kind: 'none' }
  | { readonly kind: 'pgs'; readonly sup: F; readonly name: string }
  | { readonly kind: 'vobsub'; readonly idx: F; readonly sub: F; readonly name: string }
  /** A VobSub `.sub` with no `.idx` beside it. */
  | { readonly kind: 'needs-idx'; readonly name: string }
  /** An `.idx` with no `.sub` beside it. */
  | { readonly kind: 'needs-sub'; readonly name: string };

/** True when a name alone says the file MIGHT be an image subtitle, so its head is worth reading. */
export function mightBeImageSubtitle(name: string): boolean {
  const ext = extensionOf(name);
  return ext === 'sup' || ext === 'idx' || ext === 'sub';
}

const baseName = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return (dot < 0 ? name : name.slice(0, dot)).toLowerCase();
};

/**
 * The image subtitle in a drop, if there is one. A drop can hold several files; the first image
 * subtitle found wins, with a pair preferred over a lone half, so a `.sup` dropped with unrelated
 * files is still read, and an `.idx`/`.sub` pair dropped with anything else is still paired.
 */
export function imageDrop<F>(files: readonly DroppedHead<F>[]): ImageDrop<F> {
  const sups: DroppedHead<F>[] = [];
  const idxs: DroppedHead<F>[] = [];
  const subs: DroppedHead<F>[] = [];
  for (const f of files) {
    // By name first: an MPEG program stream named .mpg or .vob starts with the same pack code
    // as a VobSub .sub, and it is a video, not a subtitle.
    if (!mightBeImageSubtitle(f.name)) continue;
    const kind = sniffBitmapFile(f.head, f.name);
    if (kind === 'pgs-sup') sups.push(f);
    else if (kind === 'vobsub-sub') subs.push(f);
    else if (kind === 'vobsub-idx') idxs.push(f);
    else if (extensionOf(f.name) === 'idx') idxs.push(f); // an .idx without its banner line
  }

  // Pairs first, by base name; then, when exactly one of each was dropped, those two whatever
  // they are called (a renamed download is still the pair the user meant).
  for (const idx of idxs) {
    const sub = subs.find((s) => baseName(s.name) === baseName(idx.name));
    if (sub) return { kind: 'vobsub', idx: idx.file, sub: sub.file, name: sub.name };
  }
  const [onlyIdx] = idxs;
  const [onlySub] = subs;
  if (idxs.length === 1 && subs.length === 1 && onlyIdx && onlySub) {
    return { kind: 'vobsub', idx: onlyIdx.file, sub: onlySub.file, name: onlySub.name };
  }
  const [sup] = sups;
  if (sup) return { kind: 'pgs', sup: sup.file, name: sup.name };
  if (onlySub) return { kind: 'needs-idx', name: onlySub.name };
  if (onlyIdx) return { kind: 'needs-sub', name: onlyIdx.name };
  return { kind: 'none' };
}
